/**
 * Inspiration photos a couple saved on a poppyflowers.com page (the Poppy Picks trend report) before
 * starting the quiz. The page sends them in one URL param; the quiz keeps them in localStorage until the
 * final submit, where they go out as `inspirationPhotos` and the proposal generator attaches them to the
 * proposal.
 *
 * Param value: base64 (URL-safe or standard) of UTF-8 JSON `[{ u: imageUrl, l: label }]`.
 */
export const INSPIRATION_BOARD_PARAM = 'inspirationBoard'
export const INSPIRATION_BOARD_KEY = 'poppyInspirationBoard'

export const MAX_BOARD_PHOTOS = 20
const MAX_LABEL_LENGTH = 200
const BOARD_TTL_MS = 30 * 24 * 60 * 60 * 1000

export type InspirationPhoto = { imageUrl: string; label: string }

type StoredBoard = { photos: InspirationPhoto[]; savedAt: number }

// Only images Poppy publishes itself, so a crafted link can't put arbitrary pictures on a proposal.
const isPoppyImageUrl = (value: unknown): value is string => {
  if (typeof value !== 'string') return false
  try {
    const url = new URL(value)
    return (
      url.protocol === 'https:' && (url.hostname === 'poppyflowers.com' || url.hostname.endsWith('.poppyflowers.com'))
    )
  } catch (err) {
    return false
  }
}

const decodeBase64Utf8 = (value: string): string => {
  // URLSearchParams turns a standard-base64 '+' into a space
  const base64 = value.replace(/ /g, '+').replace(/-/g, '+').replace(/_/g, '/')
  const binary = atob(base64)
  return decodeURIComponent(
    Array.from(binary, (char) => '%' + char.charCodeAt(0).toString(16).padStart(2, '0')).join('')
  )
}

export const parseInspirationBoard = (value: string | null | undefined): InspirationPhoto[] => {
  if (!value) return []
  let entries: unknown
  try {
    entries = JSON.parse(decodeBase64Utf8(value))
  } catch (err) {
    return []
  }
  if (!Array.isArray(entries)) return []

  const seen = new Set<string>()
  const photos: InspirationPhoto[] = []
  for (const entry of entries) {
    const imageUrl = entry?.u
    if (!isPoppyImageUrl(imageUrl) || seen.has(imageUrl)) continue
    seen.add(imageUrl)
    const label = typeof entry?.l === 'string' ? entry.l.trim().slice(0, MAX_LABEL_LENGTH) : ''
    photos.push({ imageUrl, label })
    if (photos.length === MAX_BOARD_PHOTOS) break
  }
  return photos
}

export const saveInspirationBoard = (photos: InspirationPhoto[], now = Date.now()) => {
  try {
    if (photos.length) {
      localStorage.setItem(INSPIRATION_BOARD_KEY, JSON.stringify({ photos, savedAt: now } as StoredBoard))
    } else {
      localStorage.removeItem(INSPIRATION_BOARD_KEY)
    }
  } catch (err) {
    // storage unavailable (private mode): the quiz still works, just without the board
  }
}

export const readInspirationBoard = (now = Date.now()): InspirationPhoto[] => {
  try {
    const stored: StoredBoard = JSON.parse(localStorage.getItem(INSPIRATION_BOARD_KEY) || 'null')
    if (!stored || !Array.isArray(stored.photos) || now - stored.savedAt > BOARD_TTL_MS) return []
    return stored.photos.filter((photo) => isPoppyImageUrl(photo?.imageUrl)).slice(0, MAX_BOARD_PHOTOS)
  } catch (err) {
    return []
  }
}

export const clearInspirationBoard = () => {
  try {
    localStorage.removeItem(INSPIRATION_BOARD_KEY)
  } catch (err) {
    // nothing stored
  }
}

/**
 * Saves the board from the URL params, if there is one, and returns the params without it. The param is
 * dropped so the image list never reaches `urlParameters`, which is copied into the proposal link and the CRM.
 */
export const captureInspirationBoard = <T extends Record<string, unknown>>(params: T): T => {
  if (!(INSPIRATION_BOARD_PARAM in params)) return params
  const { [INSPIRATION_BOARD_PARAM]: board, ...rest } = params
  saveInspirationBoard(parseInspirationBoard(typeof board === 'string' ? board : ''))
  return rest as T
}
